import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Box, Modal, TextField, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { Button } from '@components/index';
import type { MfaStep as MfaStepType } from '@pages/customer/common/sign/signIn/SignIn';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { CheckCircleIcon } from '@icons/CheckCircleIcon';
import { XIcon } from '@icons/index';
import { useResetMyMfa } from '@queryHooks/useMfa';
import { getErrorMessage } from '@locales/i18nErrorMessage';

type MfaStep = 'DETAIL_VIEW' | 'DISABLE_CONFIRM';

interface MFASettingModalProps {
  open: boolean;
  onClose: () => void;
  mfaEnabled: boolean;
  mfaSetupDate?: string;
  mfaStep?: MfaStep;
  onChangeMfaStep?: (step: MfaStepType) => void;
}

export const MFASettingModal = ({
  open,
  onClose,
  mfaEnabled,
  mfaSetupDate,
  mfaStep = 'DETAIL_VIEW',
  onChangeMfaStep,
}: MFASettingModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    uiStore: { toastStore },
  } = useStore();

  const [currentStep, setCurrentStep] = useState<MfaStep>(mfaStep);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const { mutate: resetMyMfa, isPending } = useResetMyMfa();

  // mfaStep props가 변경될 때 currentStep 동기화
  useEffect(() => {
    if (open) {
      setCurrentStep(mfaStep);
    }
  }, [open, mfaStep]);

  const handleClose = () => {
    setPassword('');
    setPasswordError('');
    setCurrentStep(mfaStep); // props의 초기값으로 리셋
    onClose();
  };

  // Step 1: MFA 상세 보기에서 해제 요청
  const handleDisableRequest = () => {
    // 비밀번호 입력 모달로 전환만
    setPassword('');
    setPasswordError(null);
    setCurrentStep('DISABLE_CONFIRM');
  };

  // Step 2: MFA 해제 확인 (비밀번호와 함께)
  const handleDisableConfirm = () => {
    if (!password) {
      setPasswordError(t('Account_Validation_Check_03'));
      return;
    }

    // 비밀번호 검증 + MFA 해제 API 호출
    resetMyMfa(password, {
      onSuccess: () => {
        setPasswordError(null);
        toastStore.open(t('Mfa.reset.success'));
        handleClose();

        // MfaVerification에서 호출된 경우 (로그인 페이지)만 navigate
        if (mfaStep === 'DISABLE_CONFIRM' && typeof onChangeMfaStep === 'function') {
          onChangeMfaStep('LOGIN');
        }
      },
      onError: error => {
        setPasswordError(getErrorMessage(error).message);
      },
    });
  };
  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setPassword(value);
  };

  const renderDetailView = () => (
    <>
      <DialogHeader>
        <HeaderTitle variant="headline6">{t('Mfa.profile.title')}</HeaderTitle>
        <CloseButton onClick={handleClose}>
          <XIcon width={24} height={24} color={theme.colors['ic-gray-main']} />
        </CloseButton>
      </DialogHeader>

      <DialogContent>
        <ContentSection>
          <DetailHeader>
            <DetailInfo>
              <InfoText>
                {mfaSetupDate
                  ? t('Mfa.profile.createdDate', {
                      date: dayjs(mfaSetupDate).format(t('DateFormat_YMD_Time_HM')),
                    })
                  : t('Mfa.profile.description')}
              </InfoText>
            </DetailInfo>
            <MfaStatusLabel enabled={mfaEnabled}>{mfaEnabled ? 'ON' : 'OFF'}</MfaStatusLabel>
          </DetailHeader>

          <MfaCard>
            <MfaCardContent>
              <MfaCardLeft>
                <MfaCardTitle>OTP</MfaCardTitle>
                <MfaCardStatus>
                  <CheckCircleIcon color={mfaEnabled ? theme.colors['ic-green'] : theme.colors['ic-gray-lighter']} />
                  <StatusText enabled={mfaEnabled}>
                    {mfaEnabled ? t('Mfa.profile.configured') : t('Mfa.profile.notConfigured')}
                  </StatusText>
                </MfaCardStatus>
              </MfaCardLeft>
              {mfaEnabled && (
                <Button variant="outline" size="small" onClick={handleDisableRequest} paddingHorizontal={16}>
                  {t('Mfa.modal.detail.disable')}
                </Button>
              )}
            </MfaCardContent>
          </MfaCard>
        </ContentSection>
      </DialogContent>

      <DialogFooter>
        {/* <CustomButton variant="contain" size="extraLarge" onClick={handleClose} paddingHorizontal={24}>
          {t('GNB_Profile_MyProfile_12')}
        </CustomButton> */}
      </DialogFooter>
    </>
  );

  const handleCancelDisable = () => {
    setPassword('');
    setPasswordError('');
    // DISABLE_CONFIRM에서 시작한 경우 모달 닫기, DETAIL_VIEW에서 시작한 경우 상세로 돌아가기
    if (mfaStep === 'DISABLE_CONFIRM') {
      handleClose();
    } else {
      setCurrentStep('DETAIL_VIEW');
    }
  };

  const renderDisableConfirm = () => (
    <>
      <DialogHeader>
        <HeaderTitle variant="headline6">{t('Mfa.modal.disable.title')}</HeaderTitle>
        <CloseButton onClick={handleCancelDisable}>
          <XIcon width={24} height={24} color={theme.colors['ic-gray-main']} />
        </CloseButton>
      </DialogHeader>

      <DialogContent>
        <DisableContentSection>
          <DisableDescription>{t('Mfa.modal.disable.description')}</DisableDescription>

          <PasswordSection>
            <PasswordDescription>{t('Mfa.modal.verifyPassword.description')}</PasswordDescription>
            <TextField
              fullWidth
              type="password"
              placeholder={t('Mfa.modal.verifyPassword.placeholder')}
              value={password}
              onChange={handleChangePassword}
              error={!!passwordError}
              helperText={passwordError}
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleDisableConfirm();
                }
              }}
            />
          </PasswordSection>
        </DisableContentSection>
      </DialogContent>

      <DialogFooter>
        <CustomButton variant="outline" size="extraLarge" onClick={handleCancelDisable} paddingHorizontal={24}>
          {t('GNB_Profile_MyProfile_11')}
        </CustomButton>
        <SubmitButton variant="contain" size="extraLarge" onClick={handleDisableConfirm} paddingHorizontal={24} isPending={isPending}>
          {t('Mfa.modal.disable.confirm')}
        </SubmitButton>
      </DialogFooter>
    </>
  );

  const getModalWidth = () => {
    return currentStep === 'DETAIL_VIEW' ? '600px' : '480px';
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalContainer width={getModalWidth()}>
        {currentStep === 'DETAIL_VIEW' ? renderDetailView() : renderDisableConfirm()}
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)<{ width: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${({ width }) => width};
  background-color: ${({ theme }) => theme.colors['bg-white']};
  border-radius: 6px;
  box-shadow: 0px 8px 10px -5px rgba(0, 0, 0, 0.08), 0px 16px 24px 2px rgba(0, 0, 0, 0.04),
    0px 6px 30px 5px rgba(0, 0, 0, 0.05);
  outline: none;
`;

const DialogHeader = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 18px 28px 18px 28px;
  border-bottom: none;
`;

const HeaderTitle = styled(Typography)`
  ${fonts.Headline6}
  color: ${({ theme }) => theme.colors['text-gray-main']};
` as typeof Typography;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

const DialogContent = styled(Box)`
  padding: 0 40px;
  margin-top: 17px;
`;

const ContentSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DialogFooter = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 24px 68px;
  margin-top: 17px;
`;

const DetailHeader = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const DetailInfo = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const InfoText = styled(Typography)`
  ${fonts.Body4}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
` as typeof Typography;

const MfaStatusLabel = styled(Box)<{ enabled: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0px 4px;
  border: 0.5px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  border-radius: 2px;
  ${fonts.Caption4}
  color: ${({ enabled, theme }) => (enabled ? theme.colors['text-white'] : theme.colors['text-gray-sub-darker'])};
  background-color: ${({ enabled, theme }) => (enabled ? theme.colors['bg-green'] : theme.colors['bg-gray-light'])};
`;

const MfaCard = styled(Box)`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  border-radius: 4px;
  padding: 12px 12px 12px 26px;
`;

const MfaCardContent = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
`;

const MfaCardLeft = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const MfaCardTitle = styled(Typography)`
  ${fonts.Body1}
  color: ${({ theme }) => theme.colors['text-gray-main']};
` as typeof Typography;

const MfaCardStatus = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const StatusText = styled.span<{ enabled: boolean }>`
  ${fonts.Body3}
  color: ${({ enabled, theme }) => (enabled ? theme.colors['text-green'] : theme.colors['text-gray-light'])};
`;

const DisableContentSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const DisableDescription = styled(Typography)`
  ${fonts.Body3}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  white-space: pre-line;
  line-height: 1.5;
` as typeof Typography;

const PasswordSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PasswordDescription = styled(Typography)`
  ${fonts.Body2}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
` as typeof Typography;

const CustomButton = styled(Button)`
  width: 168px;
`;

const SubmitButton = styled(SecureSubmitButton)`
  width: 168px;
`;
